import _ from 'lodash';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { filter, first, throttleTime } from 'rxjs/operators';
import { selectVirtualEventData } from 'app/virtual-event/features/data/data.selectors';
import { SendChatMessage, UpdateChatStatus } from '../../features/chat.actions';
import { selectChatMessages } from '../../features/chat.selectors';

import { EmojiButton } from '@joeattardi/emoji-button';
import { NgScrollbar } from 'ngx-scrollbar';
import { loadUser } from 'app/core/features/user/user.actions';
import { asyncScheduler, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChatComponent implements OnInit, AfterViewInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  @ViewChild('body', { static: true}) bodyRef: ElementRef;

  @ViewChild(NgScrollbar) scrollRef: NgScrollbar;
  public invertY: boolean = true;
  public style: any = {};

  public virtualEventData$ = this.store.pipe(
    select(selectVirtualEventData)
  );
  public messages$ = this.store.pipe(
    select(selectChatMessages),
    throttleTime(1000, asyncScheduler, {
      leading: true,
      trailing: true
    })
  );
  public messageStyles = {
    width: '310px'
  };

  public control: UntypedFormControl = new UntypedFormControl('', [Validators.required]);
  public picker = new EmojiButton({
    position: 'top-end'
  });

  public position = 0;
  public isNotBlockedScrolled = true;
  public showScrollToBottom = false;

  public isInitialized = false;
  public takeUntilSubject: Subject<any> = new Subject();

  constructor(
    public store: Store,
    public cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.onPickerEmoji = this.onPickerEmoji.bind(this);
    this.onValidateResize = this.onValidateResize.bind(this);
  }

  ngOnInit(): void {
    this.picker.on('emoji', this.onPickerEmoji);

    this.store.dispatch({
      type: loadUser.type
    });
  }

  ngAfterViewInit(): void {
    this.messageStyles = {
      width: `${this.bodyRef.nativeElement.clientWidth - 40}px`
    }

    this.scrollRef.scrolled.pipe(
      filter(() => this.isNotBlockedScrolled),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => {
      const target = event.target as HTMLElement;
      this.position = target.scrollHeight - target.offsetHeight - target.scrollTop;
      this.showScrollToBottom = this.position > 89;
    });

    this.isInitialized = true;
    this.onScrollToBottom();
  }

  onAfterViewInitCallback({ index, scrollToBottom }) {
    if(index < 5) { return; };

    if(scrollToBottom) {
      return this.onScrollToBottom();
    }

    if(this.position < 89) {
      return this.onScrollToBottom();
    }

    this.showScrollToBottom = true;
  }

  ngOnDestroy(): void {
    this.picker.off('emoji', this.onPickerEmoji);

    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();
  }

  onPickerEmoji(selection) {
    // `selection` object has an `emoji` property
    // containing the selected emoji
    this.control.setValue(`${this.control.value} ${selection.emoji} `);
  }

  onClickEmoji(event) {
    this.picker.togglePicker(event.target)
  }

  onSendChatMessage(event, message) {
    const { control } = this;
    if(control.invalid) {
      return;
    }

    this.store.pipe(
      select(selectVirtualEventData),
      first(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ id }) => {
      this.store.dispatch({
        type: SendChatMessage.type,
        payload: { id: id, message }
      });
      control.setValue('');
    })
  }

  onSubmit(event) {
    this.onSendChatMessage(event, this.control.value);
  }

  onClose() {
    this.store.dispatch({
      type: UpdateChatStatus.type,
      status: 'closed'
    });
  }

  onScrollToBottom() {
    if(this.isInitialized === false) {
      return;
    }
    
    this.isNotBlockedScrolled = false;
    return this.scrollRef.scrollTo({ bottom: 0 }).then(() => {
      this.isNotBlockedScrolled = true;
      this.showScrollToBottom = false;
      this.cdr.markForCheck();
    });
  }

  onResizeEnd(event) {
    const { rectangle } = event;
    this.style = {
      position: 'fixed',
      left: `${rectangle.left}px`,
      top: `${rectangle.top}px`,
      width: `${rectangle.width}px`,
      height: `${rectangle.height}px`
    };
  }

  onValidateResize(event) {
    const { rectangle } = event;
    if(rectangle.height < 200) {
      return false;
    }
    if(rectangle.width < 250) {
      return false;
    }
    this.ngZone.run(() => {
      this.messageStyles = {
        width: `${rectangle.width - 40}px`
      };
    })
    return true;
  }

}
